import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  czyBladWczytaniaModulu,
  czyMoznaPrzeladowac,
  czySerwerMaNowszyBuild,
  przeladujPoBledzieModulu,
} from '@/lib/samonaprawaWdrozenia';

/**
 * Drugi mechanizm samonaprawy: ZANIM coś padnie (etap L).
 *
 * `ErrorBoundary` ratuje sytuację, w której brak kawałka aplikacji już wywrócił render. Tu
 * chodzi o moment wcześniejszy: człowiek wraca do karty zostawionej na godzinę, my w tym
 * czasie wdrożyliśmy cztery razy. Przy powrocie sprawdzamy, czy serwer podaje inny plik
 * wejściowy niż ten, który mamy załadowany; jeśli tak, przeładowujemy przy NAJBLIŻSZEJ
 * nawigacji — czyli w chwili, w której człowiek i tak zmienia stronę.
 *
 * DLACZEGO NIE OD RAZU PO POWROCIE. Przeładowanie pod palcem w środku czytania oferty jest
 * gorsze niż problem, który naprawia: znika pozycja przewinięcia, znika otwarty arkusz,
 * a człowiek nie prosił o nic. Przy nawigacji i tak wszystko się zmienia, więc koszt jest
 * zerowy.
 *
 * Łapiemy też błędy, które nie przechodzą przez React: `unhandledrejection` z odrzuconego
 * `import()` i `error` ze skryptu, którego nie dało się pobrać.
 */
export default function SamonaprawaWdrozenia() {
  const location = useLocation();
  const nowyBuild = useRef(false);
  const pierwszaSciezka = useRef(location.pathname);

  useEffect(() => {
    const sprawdz = async () => {
      if (document.visibilityState !== 'visible' || nowyBuild.current) return;
      const inny = await czySerwerMaNowszyBuild();
      if (inny) nowyBuild.current = true;
    };
    document.addEventListener('visibilitychange', sprawdz);

    const naOdrzucenie = (e: PromiseRejectionEvent) => {
      if (czyBladWczytaniaModulu(e.reason) && czyMoznaPrzeladowac()) void przeladujPoBledzieModulu(e.reason);
    };
    const naBlad = (e: ErrorEvent) => {
      if (czyBladWczytaniaModulu(e.error ?? e.message) && czyMoznaPrzeladowac()) {
        void przeladujPoBledzieModulu(e.error ?? e.message);
      }
    };
    window.addEventListener('unhandledrejection', naOdrzucenie);
    window.addEventListener('error', naBlad);
    return () => {
      document.removeEventListener('visibilitychange', sprawdz);
      window.removeEventListener('unhandledrejection', naOdrzucenie);
      window.removeEventListener('error', naBlad);
    };
  }, []);

  useEffect(() => {
    // Pierwsze wejście nie jest nawigacją — inaczej przeładowalibyśmy stronę, którą człowiek
    // dopiero otworzył.
    if (location.pathname === pierwszaSciezka.current) return;
    if (!nowyBuild.current) return;
    window.location.assign(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}
